import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Eye, EyeOff, X, Copy, Check, CheckSquare, FolderOpen, Trash2, Sparkles, FileSpreadsheet, Upload, Store, Filter, Square, Package, Download, Wand2, Edit2, Palette, Image as ImageIcon, Ruler, Camera, ChevronLeft, ChevronRight, ChevronFirst, ChevronLast, Combine, BookOpen, Loader2, Tag } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import AdminPageHeader from '../../components/AdminPageHeader';
import QuickImportDialog from '../../components/admin/QuickImportDialog';
import ExcelImportDialog from '../../components/admin/ExcelImportDialog';
import ProductSyncDialog from '../../components/admin/ProductSyncDialog';
import BulkImageUploadDialog from '../../components/admin/BulkImageUploadDialog';
import ImageLibraryDialog from '../../components/admin/ImageLibraryDialog';
import CatalogDialog from '../../components/CatalogDialog';
import BulkActionsToolbar from '../../components/admin/BulkActionsToolbar';
import ProductImageEditor from '../../components/ProductImageEditor';
import BatchImageProcessor from '../../components/BatchImageProcessor';
import LabelPreviewDialog from '../../components/LabelPreviewDialog';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategoryConfig, setSubcategoryConfig] = useState({});
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showQuickImport, setShowQuickImport] = useState(false);
  const [showExcelImport, setShowExcelImport] = useState(false);
  const [showProductSync, setShowProductSync] = useState(false);
  const [showBulkImageUpload, setShowBulkImageUpload] = useState(false);
  const [bulkImageProduct, setBulkImageProduct] = useState(null);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  // Image drag & drop reordering state
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  
  // Image Editor states
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [editingImageUrl, setEditingImageUrl] = useState('');
  const [editingImageFile, setEditingImageFile] = useState(null);
  
  // Batch Image Processor
  const [showBatchProcessor, setShowBatchProcessor] = useState(false);
  const [showCatalogDialog, setShowCatalogDialog] = useState(false);
  
  // Filter states
  const [ownerFilter, setOwnerFilter] = useState('all'); // 'all', 'admin', 'vendors', or vendor_id
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [handmadeFilter, setHandmadeFilter] = useState('all'); // 'all', 'handmade', 'commercial'
  const [searchTerm, setSearchTerm] = useState('');

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Bulk-Delete intentionally removed — user never wants to mass-delete products.
  // Individual product deletion is still available via the row's trash icon.

  const handleBulkMoveToCategory = async (category) => {
    if (selectedProducts.length === 0) {
      toast.error('Keine Produkte ausgewählt');
      return;
    }

    try {
      await Promise.all(
        selectedProducts.map(id => 
          axiosInstance.put(`/products/${id}`, { category })
        )
      );
      toast.success(`${selectedProducts.length} Produkt(e) in Kategorie "${category}" verschoben`);
      setSelectedProducts([]);
      setShowBulkActions(false);
      fetchProducts();
    } catch (error) {
      toast.error('Fehler beim Verschieben der Produkte');
      console.error(error);
    }
  };
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', sale_price: '', sale_starts_at: '', sale_ends_at: '', category: '', subcategory: '',
    images: '', stock: '', is_handmade: false, delivery_time: '1-3', weight: '', length: '', width: '', height: '', sku: '', unit: 'Stück',
    color_variants: [],
    price_type: 'per_piece', price_quantity: 1,
    meta_title: '', meta_description: '',
  });
  const [colorVariants, setColorVariants] = useState([]);
  const [seoGenerating, setSeoGenerating] = useState(false);

  const handleGenerateSeo = async () => {
    if (!formData.name?.trim() && !formData.description?.trim()) {
      toast.error('Bitte zuerst Name oder Beschreibung ausfüllen.');
      return;
    }
    setSeoGenerating(true);
    try {
      const { data } = await axiosInstance.post('/seo/generate', {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        color: colorVariants[0]?.color_name || '',
        price: parseFloat(formData.price) || 0,
        is_handmade: !!formData.is_handmade,
      });
      setFormData((prev) => ({
        ...prev,
        meta_title: data.meta_title || prev.meta_title,
        meta_description: data.meta_description || prev.meta_description,
      }));
      toast.success('KI-Vorschlag übernommen');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'KI nicht erreichbar';
      toast.error(msg);
    } finally {
      setSeoGenerating(false);
    }
  };
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateProducts, setDuplicateProducts] = useState([]);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  // Merge dialog state
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeKeepId, setMergeKeepId] = useState('');
  const [mergeColorOverrides, setMergeColorOverrides] = useState({}); // {productId: 'color name'}
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchVendors();
    axiosInstance.get('/categories/subcategory-config').then(r => setSubcategoryConfig(r.data?.config || {})).catch(() => {});
  }, []);

  // Filter products when filter or search changes
  useEffect(() => {
    let filtered = [...products];
    
    // Filter by owner
    if (ownerFilter === 'admin') {
      filtered = filtered.filter(p => !p.vendor_id);
    } else if (ownerFilter === 'vendors') {
      filtered = filtered.filter(p => p.vendor_id);
    } else if (ownerFilter && ownerFilter !== 'all') {
      // Specific vendor
      filtered = filtered.filter(p => p.vendor_id === ownerFilter);
    }
    
    // Filter by active/inactive status
    if (statusFilter === 'active') {
      filtered = filtered.filter(p => p.is_active);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(p => !p.is_active);
    }

    // Filter handmade vs commercial
    if (handmadeFilter === 'handmade') {
      filtered = filtered.filter(p => p.is_handmade);
    } else if (handmadeFilter === 'commercial') {
      filtered = filtered.filter(p => !p.is_handmade);
    }
    
    // Filter by search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(term) ||
        p.sku?.toLowerCase().includes(term) ||
        p.vendor_shop_name?.toLowerCase().includes(term)
      );
    }
    
    setFilteredProducts(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [products, ownerFilter, searchTerm, statusFilter, handmadeFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/products?active_only=false&include_vendor_info=true');
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Produkte');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axiosInstance.get('/vendors/admin/all');
      const approvedVendors = response.data.filter(v => v.vendor_profile?.status === 'approved');
      setVendors(approvedVendors);
    } catch (error) {
      console.error('Fehler beim Laden der Verkäufer:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien:', error);
      setCategories([]);
    }
  };

  const handleCreateNewCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Bitte geben Sie einen Kategorienamen ein');
      return;
    }

    try {
      await axiosInstance.post('/admin/categories', {
        name: newCategoryName.trim(),
        description: `Kategorie: ${newCategoryName.trim()}`,
        image: ''
      });
      
      toast.success(`Kategorie "${newCategoryName}" erstellt!`);
      setFormData({ ...formData, category: newCategoryName.trim() });
      setNewCategoryName('');
      setShowNewCategoryInput(false);
      
      // Kategorien neu laden
      fetchCategories();
    } catch (error) {
      toast.error('Fehler beim Erstellen der Kategorie');
    }
  };

  // Convert ISO string to "datetime-local" format (YYYY-MM-DDTHH:MM, local timezone)
  const isoToLocal = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price,
      sale_price: product.sale_price ?? '',
      sale_starts_at: isoToLocal(product.sale_starts_at),
      sale_ends_at: isoToLocal(product.sale_ends_at),
      category: product.category,
      subcategory: product.subcategory || '',
      images: product.images?.join('\n') || '',
      stock: product.stock,
      color_variants: product.color_variants || [],
      is_handmade: product.is_handmade,
      delivery_time: product.delivery_time || '1-3',
      weight: product.weight || '',
      length: product.length || '',
      width: product.width || '',
      height: product.height || '',
      sku: product.sku || '',
      unit: product.unit || 'Stück',
      price_type: product.price_type || 'per_piece',
      price_quantity: product.price_quantity || 1,
      meta_title: product.meta_title || '',
      meta_description: product.meta_description || ''
    });
    setColorVariants(product.color_variants || []);
    setShowDialog(true);
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setColorVariants([]);
    setFormData({
      name: '', description: '', price: '', sale_price: '', sale_starts_at: '', sale_ends_at: '', category: '', subcategory: '',
      images: '', stock: '', is_handmade: false, delivery_time: '1-3', weight: '', length: '', width: '', height: '', sku: '', unit: 'Stück',
      price_type: 'per_piece', price_quantity: 1,
      meta_title: '', meta_description: ''
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e, skipDuplicateCheck = false) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        subcategory: formData.subcategory || null,
        price: parseFloat(formData.price),
        sale_price: formData.sale_price && parseFloat(formData.sale_price) > 0 ? parseFloat(formData.sale_price) : null,
        sale_starts_at: formData.sale_starts_at ? new Date(formData.sale_starts_at).toISOString() : null,
        sale_ends_at: formData.sale_ends_at ? new Date(formData.sale_ends_at).toISOString() : null,
        price_type: formData.price_type || 'per_piece',
        price_quantity: formData.price_type === 'per_quantity' ? parseInt(formData.price_quantity) || 1 : 1,
        images: formData.images.split('\n').filter(url => url.trim()),
        stock: parseInt(formData.stock),
        is_handmade: formData.is_handmade,
        delivery_time: formData.delivery_time || '1-3',
        sku: formData.sku || null,
        unit: formData.unit || 'Stück',
        weight: formData.weight ? parseFloat(formData.weight) : null,
        length: formData.length ? parseFloat(formData.length) : null,
        width: formData.width ? parseFloat(formData.width) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        color_variants: colorVariants.filter(v => v.color_name.trim() !== '').map((v, idx) => ({
          ...v,
          // Auto-generate SKU suffix from color name
          sku_suffix: v.color_name ? `-${v.color_name.substring(0, 3).toUpperCase()}` : `-V${idx + 1}`
        })),
        meta_title: formData.meta_title || null,
        meta_description: formData.meta_description || null,
      };

      // Check for duplicates only when creating new products (not editing)
      if (!editingProduct && !skipDuplicateCheck) {
        const duplicateCheck = await axiosInstance.post('/products/check-duplicate', data);
        
        if (duplicateCheck.data.is_duplicate) {
          setDuplicateProducts(duplicateCheck.data.similar_products);
          setPendingSubmit(data);
          setShowDuplicateWarning(true);
          return; // Stop here and show warning
        }
      }

      // Proceed with save
      if (editingProduct) {
        await axiosInstance.put(`/products/${editingProduct.id}`, data);
        toast.success('Produkt aktualisiert');
      } else {
        await axiosInstance.post('/products', data);
        toast.success('Produkt erstellt');
      }
      // SALE-Bestätigung: dem Admin sofort sichtbar machen, dass der Rabatt überall live ist
      if (data.sale_price && data.price && data.sale_price < data.price) {
        const pct = Math.round((1 - data.sale_price / data.price) * 100);
        toast.success(
          `🔥 −${pct}% Rabatt aktiv · jetzt live im SALE-Banner, Shop & /sale`,
          { duration: 5000 }
        );
      } else if (editingProduct && editingProduct.sale_price && (!data.sale_price || data.sale_price >= data.price)) {
        toast.info('SALE-Preis entfernt · Artikel erscheint wieder zum Normalpreis');
      }
      setShowDialog(false);
      setShowDuplicateWarning(false);
      setPendingSubmit(null);
      fetchProducts();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleConfirmDuplicate = async () => {
    if (pendingSubmit) {
      try {
        await axiosInstance.post('/products', pendingSubmit);
        toast.success('Produkt erstellt');
        setShowDialog(false);
        setShowDuplicateWarning(false);
        setPendingSubmit(null);
        fetchProducts();
      } catch (error) {
        toast.error('Fehler beim Speichern');
      }
    }
  };

  const handleToggleActive = async (product) => {
    try {
      await axiosInstance.put(`/products/${product.id}`, { is_active: !product.is_active });
      toast.success(product.is_active ? 'Produkt deaktiviert' : 'Produkt aktiviert');
      fetchProducts();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // --- Merge products as color variants ---
  const handleOpenMergeDialog = () => {
    if (selectedProducts.length < 2) {
      toast.error('Bitte mindestens 2 Artikel auswählen, um sie zusammenzuführen');
      return;
    }
    // Default master = first selected (the one most recently added to the selection)
    const defaultMaster = selectedProducts[0];
    setMergeKeepId(defaultMaster);
    // Pre-fill suggested color names by stripping common prefix with master name
    const master = products.find(p => p.id === defaultMaster);
    const overrides = {};
    selectedProducts.forEach(id => {
      if (id === defaultMaster) return;
      const src = products.find(p => p.id === id);
      if (!src || !master) return;
      // strip common leading words
      const mw = master.name.toLowerCase().split(/\s+/);
      const sw = src.name.split(/\s+/);
      let cut = 0;
      for (let i = 0; i < Math.min(mw.length, sw.length); i++) {
        if (sw[i].toLowerCase() === mw[i]) cut = i + 1; else break;
      }
      const label = sw.slice(cut).join(' ').trim() || src.name;
      overrides[id] = label;
    });
    setMergeColorOverrides(overrides);
    setShowMergeDialog(true);
  };

  const handleConfirmMerge = async () => {
    if (!mergeKeepId) {
      toast.error('Bitte einen Haupt-Artikel wählen');
      return;
    }
    const merge_ids = selectedProducts.filter(id => id !== mergeKeepId);
    if (merge_ids.length === 0) {
      toast.error('Mindestens 1 weiterer Artikel nötig');
      return;
    }
    try {
      const res = await axiosInstance.post('/products/merge', {
        keep_id: mergeKeepId,
        merge_ids,
        color_overrides: mergeColorOverrides,
      });
      const { merged_count, variants_total, stock_added } = res.data;
      toast.success(
        `${merged_count} Artikel als Farbvarianten zusammengeführt (gesamt ${variants_total} Varianten, +${stock_added} Bestand).`
      );
      setShowMergeDialog(false);
      setMergeKeepId('');
      setMergeColorOverrides({});
      setSelectedProducts([]);
      await fetchProducts();
    } catch (e) {
      toast.error('Zusammenführen fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDeleteSingle = async (product) => {
    const ok = window.confirm(
      `Artikel „${product.name}" wirklich löschen?\n\n` +
      `Diese Aktion kann nicht rückgängig gemacht werden.\n` +
      `Tipp: Falls du den Artikel später wieder brauchst, klicke stattdessen auf das orange Auge (Deaktivieren).`
    );
    if (!ok) return;
    try {
      await axiosInstance.delete(`/products/${product.id}`);
      toast.success(`„${product.name}" gelöscht`);
      fetchProducts();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axiosInstance.get('/products/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `produkte_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export erfolgreich');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fehler beim Export');
    }
  };

  // Etikett-PDF-Vorschau-Dialog
  const [labelPreviewMode, setLabelPreviewMode] = useState(null); // 'single' | 'all' | null
  const [labelPreviewProduct, setLabelPreviewProduct] = useState(null);

  // Etikett-PDF (40 × 30 mm) für einen Artikel — öffnet Vorschau-Dialog
  const handleLabelSingle = (product) => {
    setLabelPreviewProduct(product);
    setLabelPreviewMode('single');
  };

  // Sammel-Etiketten (alle aktiven Artikel)
  const handleLabelsAll = () => {
    setLabelPreviewProduct(null);
    setLabelPreviewMode('all');
  };

  const checkImageQuality = (file) => new Promise((resolve) => {
    // Zeigt eine sanfte Warnung wenn das Bild < 1200 px in der längsten Seite ist.
    // Uploads werden NICHT geblockt — die Warnung informiert nur.
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const maxDim = Math.max(img.naturalWidth, img.naturalHeight);
      URL.revokeObjectURL(objectUrl);
      if (maxDim < 1200) {
        toast.warning(
          `Bild hat nur ${img.naturalWidth}×${img.naturalHeight} px. Für Print & SEO empfehlen wir ≥ 1200 px auf der längsten Seite.`,
          { duration: 6000 }
        );
      }
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    img.src = objectUrl;
  });

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wählen Sie eine Bilddatei aus');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bild muss kleiner als 5MB sein');
      return;
    }

    // Sanfte Qualitäts-Warnung (blockiert nicht)
    await checkImageQuality(file);

    // Open image editor with the file
    setEditingImageFile(file);
    setEditingImageUrl(URL.createObjectURL(file));
    setEditingImageIndex(null); // null = new image
    setShowImageEditor(true);
    
    // Reset file input
    event.target.value = '';
  };

  // Direct camera upload without editor
  const handleCameraCapture = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wählen Sie eine Bilddatei aus');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bild muss kleiner als 5MB sein');
      return;
    }

    // Sanfte Qualitäts-Warnung (blockiert nicht)
    await checkImageQuality(file);

    setUploadingImage(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const uploadResponse = await axiosInstance.post('/uploads/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (uploadResponse.data.url) {
        let imageUrl = uploadResponse.data.url;
        if (imageUrl.startsWith('/api')) {
          imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
        }

        const currentImages = formData.images ? formData.images.split('\n').filter(i => i.trim()) : [];
        currentImages.push(imageUrl);

        setFormData(prev => ({
          ...prev,
          images: currentImages.join('\n')
        }));

        toast.success('Foto erfolgreich hinzugefügt!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Fehler beim Hochladen des Fotos');
    } finally {
      setUploadingImage(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' }, // Use back camera on mobile
        audio: false 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setShowCamera(true);
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Kamera konnte nicht gestartet werden. Bitte erlauben Sie den Kamerazugriff.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const capturePhoto = async () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    // Convert to blob
    canvas.toBlob(async (blob) => {
      if (!blob) {
        toast.error('Fehler beim Aufnehmen des Fotos');
        return;
      }

      stopCamera();
      setUploadingImage(true);

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', blob, `camera-photo-${Date.now()}.jpg`);

        const uploadResponse = await axiosInstance.post('/uploads/upload', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (uploadResponse.data.url) {
          let imageUrl = uploadResponse.data.url;
          if (imageUrl.startsWith('/api')) {
            imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
          }

          const currentImages = formData.images ? formData.images.split('\n').filter(i => i.trim()) : [];
          currentImages.push(imageUrl);

          setFormData({
            ...formData,
            images: currentImages.join('\n')
          });

          toast.success('Foto erfolgreich aufgenommen!');
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Fehler beim Hochladen des Fotos');
      } finally {
        setUploadingImage(false);
      }
    }, 'image/jpeg', 0.9);
  };

  const handleEditExistingImage = (imageUrl, index) => {
    setEditingImageFile(null);
    setEditingImageUrl(imageUrl);
    setEditingImageIndex(index);
    setShowImageEditor(true);
  };

  const handleSaveEditedImage = async (processedImageData) => {
    try {
      // If it's a base64 data URL, we need to upload it
      if (processedImageData.startsWith('data:')) {
        // Convert base64 to blob
        const response = await fetch(processedImageData);
        const blob = await response.blob();
        
        const formDataUpload = new FormData();
        formDataUpload.append('file', blob, 'processed-image.png');
        
        const uploadResponse = await axiosInstance.post('/uploads/upload', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (uploadResponse.data.url) {
          // Make sure we have the full URL
          let imageUrl = uploadResponse.data.url;
          if (imageUrl.startsWith('/api')) {
            imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
          }
          
          const currentImages = formData.images ? formData.images.split('\n').filter(i => i.trim()) : [];
          
          if (editingImageIndex !== null) {
            // Replace existing image
            currentImages[editingImageIndex] = imageUrl;
          } else {
            // Add new image
            currentImages.push(imageUrl);
          }
          
          setFormData({
            ...formData,
            images: currentImages.join('\n')
          });
          
          toast.success('Bild erfolgreich gespeichert!');
        }
      }
    } catch (error) {
      console.error('Error saving edited image:', error);
      toast.error('Fehler beim Speichern des Bildes');
    }
  };

  const handleDuplicate = async (productId) => {
    if (!window.confirm('Möchten Sie dieses Produkt duplizieren?')) return;
    
    try {
      const response = await axiosInstance.post(`/products/${productId}/duplicate`);
      toast.success('Produkt wurde dupliziert');
      fetchProducts(); // Reload products list
    } catch (error) {
      toast.error('Fehler beim Duplizieren des Produkts');
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-7xl mx-auto">
        <AdminPageHeader 
          title="Produktverwaltung" 
          subtitle={`${products.length} Produkte${selectedProducts.length > 0 ? ` - ${selectedProducts.length} ausgewählt` : ''}`}
          icon={Package}
        />
        
        {/* Section: Actions - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFF8F0]">
            <Sparkles className="h-4 w-4 text-[#E89860]" />
          </div>
          <h2 className="text-base font-semibold text-white">Aktionen</h2>
        </div>

        {/* Toolbar - Beige themed - Mobile-optimiert */}
        <div className="bg-[#F5EDE4] border border-[#C4A084] rounded-none p-3 mb-4">
          {/* Bulk Actions - Wenn Produkte ausgewählt (kein Massen-Löschen, das ist Absicht) */}
          {selectedProducts.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-[#C4A084]">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowBulkActions(true)}
                className="bg-[#F0F8FF] hover:bg-[#E0F0FF] text-[#60A0D0] border-[#C4A084] text-xs h-8"
              >
                <FolderOpen className="h-3.5 w-3.5 mr-1" />
                Kategorie
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenMergeDialog}
                disabled={selectedProducts.length < 2}
                className="bg-[#F5EDE4] hover:bg-[#EBE0D4] text-[#5D4E3C] border-[#C4A084] text-xs h-8 disabled:opacity-50"
                data-testid="merge-products-btn"
              >
                <Combine className="h-3.5 w-3.5 mr-1" />
                Zusammenführen
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedProducts([])}
                className="bg-[#FAF8F5] hover:bg-[#F0EDE8] text-[#A09080] border-[#C4A084] text-xs h-8"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Abwählen
              </Button>
            </div>
          )}

          {/* Main Actions - Grid für Mobile mit Pastellfarben */}
          <div className="grid grid-cols-3 sm:grid-cols-8 gap-2">
            {selectedProducts.length === 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                className="h-10 bg-[#F0FFF0] hover:bg-[#E0FFE0] border-[#C4A084] text-[#60B060] rounded-none text-xs flex flex-col items-center justify-center gap-0.5 p-1"
              >
                <CheckSquare className="h-4 w-4" />
                <span className="text-[10px]">Alle</span>
              </Button>
            )}
            <Button 
              onClick={() => setShowQuickImport(true)} 
              variant="outline"
              className="h-10 bg-[#FFF8F0] hover:bg-[#FFEFE0] border-[#C4A084] text-[#E89860] rounded-none text-xs flex flex-col items-center justify-center gap-0.5 p-1"
              size="sm"
            >
              <Sparkles className="h-4 w-4" />
              <span className="text-[10px]">Import</span>
            </Button>
            <Button 
              onClick={() => setShowExcelImport(true)} 
              variant="outline"
              className="h-10 bg-[#F0F8FF] hover:bg-[#E0F0FF] border-[#C4A084] text-[#60A0D0] rounded-none text-xs flex flex-col items-center justify-center gap-0.5 p-1"
              size="sm"
            >
              <Upload className="h-4 w-4" />
              <span className="text-[10px]">Excel</span>
            </Button>
            <Button
              onClick={() => setShowProductSync(true)}
              variant="outline"
              className="h-10 bg-[#F0FFF8] hover:bg-[#E0FFF0] border-[#C4A084] text-[#4A9080] rounded-none text-xs flex flex-col items-center justify-center gap-0.5 p-1"
              size="sm"
              title="Preview ↔ Prod Sync via JSON"
              data-testid="open-product-sync"
            >
              <Combine className="h-4 w-4" />
              <span className="text-[10px]">Sync</span>
            </Button>
            <Button
              onClick={() => setShowImageLibrary(true)}
              variant="outline"
              className="h-10 bg-[#E0F7FA] hover:bg-[#B2EBF2] border-[#C4A084] text-[#00838F] rounded-none text-xs flex flex-col items-center justify-center gap-0.5 p-1"
              size="sm"
              title="Bild-Bibliothek — alle bereits hochgeladenen Bilder"
              data-testid="open-image-library"
            >
              <ImageIcon className="h-4 w-4" />
              <span className="text-[10px]">Bilder</span>
            </Button>
            <Button 
              onClick={() => handleExport()} 
              variant="outline"
              className="h-10 bg-[#F8F0FF] hover:bg-[#F0E0FF] border-[#C4A084] text-[#A070D0] rounded-none text-xs flex flex-col items-center justify-center gap-0.5 p-1"
              size="sm"
            >
              <Download className="h-4 w-4" />
              <span className="text-[10px]">Export</span>
            </Button>
            <Button 
              onClick={() => setShowBatchProcessor(true)} 
              variant="outline"
              className="h-10 bg-[#FFF5E6] hover:bg-[#FFEDD4] border-[#C4A084] text-[#AF9774] rounded-none text-xs flex flex-col items-center justify-center gap-0.5 p-1"
              size="sm"
              title="Mehrere Bilder gleichzeitig bearbeiten"
            >
              <Wand2 className="h-4 w-4" />
              <span className="text-[10px]">Bilder</span>
            </Button>
            <Button
              onClick={() => setShowCatalogDialog(true)}
              variant="outline"
              className="h-10 bg-[#EFE5D7] hover:bg-[#E5D4BA] border-[#C4A084] text-[#5D4E3C] rounded-none text-xs flex flex-col items-center justify-center gap-0.5 p-1"
              size="sm"
              title="PDF-Katalog automatisch erstellen"
              data-testid="open-catalog-dialog"
            >
              <BookOpen className="h-4 w-4" />
              <span className="text-[10px]">Katalog</span>
            </Button>
            <Button
              onClick={handleLabelsAll}
              variant="outline"
              className="h-10 bg-[#FFF8E6] hover:bg-[#FFEFCC] border-[#C4A084] text-[#A07F2E] rounded-none text-xs flex flex-col items-center justify-center gap-0.5 p-1"
              size="sm"
              title="Etiketten (40×30 mm) aller Artikel als A4-PDF"
              data-testid="open-labels-all"
            >
              <Tag className="h-4 w-4" />
              <span className="text-[10px]">Etiketten</span>
            </Button>
            <Button 
              onClick={handleCreate} 
              className="h-10 bg-[#FFF0F5] hover:bg-[#FFE0EB] text-[#E07090] border border-[#C4A084] rounded-none text-xs flex flex-col items-center justify-center gap-0.5 p-1 col-span-2 sm:col-span-1"
              size="sm"
            >
              <Plus className="h-4 w-4" />
              <span className="text-[10px]">Neu</span>
            </Button>
          </div>
        </div>

        {/* Catalog Generator Dialog */}
        <CatalogDialog
          isOpen={showCatalogDialog}
          onClose={() => setShowCatalogDialog(false)}
        />

        {/* Label Preview Dialog */}
        <LabelPreviewDialog
          open={labelPreviewMode !== null}
          mode={labelPreviewMode || 'single'}
          product={labelPreviewProduct}
          onClose={() => { setLabelPreviewMode(null); setLabelPreviewProduct(null); }}
        />

        {/* Quick Import Dialog */}
        <QuickImportDialog
          isOpen={showQuickImport}
          onClose={() => setShowQuickImport(false)}
          onProductsCreated={() => {
            fetchProducts();
            setShowQuickImport(false);
          }}
          categories={categories.map(c => c.name || c)}
        />

        {/* Excel Import Dialog */}
        <ExcelImportDialog
          isOpen={showExcelImport}
          onClose={() => setShowExcelImport(false)}
          onImportComplete={() => {
            fetchProducts();
          }}
        />

        {/* Product Sync Dialog (Preview ↔ Prod) */}
        <ProductSyncDialog
          isOpen={showProductSync}
          onClose={() => setShowProductSync(false)}
          onSyncComplete={fetchProducts}
        />

        {/* Bulk Image Upload — mehrere Bilder auf einmal zu einem Produkt */}
        <BulkImageUploadDialog
          isOpen={showBulkImageUpload}
          onClose={() => { setShowBulkImageUpload(false); setBulkImageProduct(null); }}
          product={bulkImageProduct}
          onUploadComplete={fetchProducts}
        />

        {/* Image Library — zentrale Übersicht aller Produktbilder */}
        <ImageLibraryDialog
          isOpen={showImageLibrary}
          onClose={() => setShowImageLibrary(false)}
          products={products}
          onAttach={fetchProducts}
        />

        {/* Section: Filter - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0F8FF]">
            <Filter className="h-4 w-4 text-[#60A0D0]" />
          </div>
          <h2 className="text-base font-semibold text-white">Filter & Suche</h2>
        </div>

        {/* Filter Bar - Beige themed */}
        <div className="bg-[#F5EDE4] border border-[#C4A084] rounded-none p-3 mb-4 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded flex items-center justify-center bg-[#F0F8FF]">
              <Filter className="h-3 w-3 text-[#60A0D0]" />
            </div>
            <span className="text-sm font-medium text-[#8B7355]">Filter:</span>
          </div>
          
          {/* Owner Filter */}
          <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className="bg-white border border-[#C4A084] rounded-none px-3 py-2 text-sm text-[#5D4E3C] focus:outline-none focus:ring-2 focus:ring-[#AF9774]"
          >
            <option value="all">👥 Alle Produkte ({products.length})</option>
            <option value="admin">Meine Produkte ({products.filter(p => !p.vendor_id).length})</option>
            <option value="vendors">Verkäufer-Produkte ({products.filter(p => p.vendor_id).length})</option>
            {vendors.length > 0 && (
              <optgroup label="Nach Verkäufer:">
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.vendor_profile?.shop_name || v.email}
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          {/* Status Filter — wichtig für 'Übersicht deaktivierter Artikel' */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border border-[#C4A084] rounded-none px-3 py-2 text-sm text-[#5D4E3C] focus:outline-none focus:ring-2 focus:ring-[#AF9774]"
            data-testid="products-status-filter"
            title="Sichtbarkeit filtern"
          >
            <option value="all">🔘 Alle Status ({products.length})</option>
            <option value="active">✅ Nur aktiv ({products.filter(p => p.is_active).length})</option>
            <option value="inactive">⏸ Nur deaktiviert ({products.filter(p => !p.is_active).length})</option>
          </select>

          {/* Handmade Filter — handgemacht vs. kommerziell */}
          <select
            value={handmadeFilter}
            onChange={(e) => setHandmadeFilter(e.target.value)}
            className="bg-white border border-[#C4A084] rounded-none px-3 py-2 text-sm text-[#5D4E3C] focus:outline-none focus:ring-2 focus:ring-[#AF9774]"
            data-testid="products-handmade-filter"
            title="Handgemacht vs. kommerziell"
          >
            <option value="all">🎨 Alle Arten ({products.length})</option>
            <option value="handmade">💝 Nur handgemacht ({products.filter(p => p.is_handmade).length})</option>
            <option value="commercial">📦 Nur kommerziell ({products.filter(p => !p.is_handmade).length})</option>
          </select>

          {/* Search */}
          <Input
            placeholder="Suche nach Name, SKU, Verkäufer…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-xs bg-white border-[#C4A084] rounded-none focus:ring-[#AF9774]"
          />
          
          {/* Result count */}
          <span className="text-sm text-[#8B7355] ml-auto">
            {filteredProducts.length} von {products.length} Produkten
          </span>
        </div>

        {/* Section: Product List - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center justify-between" style={{ backgroundColor: '#C4A084' }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFFEF5]">
              <Package className="h-4 w-4 text-[#D4B030]" />
            </div>
            <h2 className="text-base font-semibold text-white">Produktliste</h2>
          </div>
          <span className="text-xs text-white/80">
            {filteredProducts.length} Produkte
          </span>
        </div>

        {loading ? (
          <div className="text-center py-12"><p className="text-gray-600">Lädt...</p></div>
        ) : (
          <>
            {/* Compact Product List */}
            <div className="bg-white border border-[#D4C4B0] rounded-none divide-y divide-[#E8E0D5]">
              {paginatedProducts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Keine Produkte gefunden
                </div>
              ) : (
                paginatedProducts.map((product) => (
                  <div 
                    key={product.id} 
                    className={`p-3 hover:bg-[#FAF8F5] cursor-pointer transition-colors ${
                      !product.is_active ? 'opacity-50 bg-gray-50' : ''
                    } ${selectedProducts.includes(product.id) ? 'bg-blue-50 ring-1 ring-blue-300' : ''}`}
                    onClick={() => handleEdit(product)}
                  >
                    {/* Top Row: Checkbox, Image, Name, Price */}
                    <div className="flex items-center gap-2">
                      {/* Checkbox */}
                      <div 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectProduct(product.id);
                        }}
                        className={`w-5 h-5 rounded-none border-2 flex items-center justify-center cursor-pointer flex-shrink-0 ${
                          selectedProducts.includes(product.id) 
                            ? 'bg-[#C4A084] border-[#C4A084]' 
                            : 'bg-white border-[#D4C4B0] hover:border-[#C4A084]'
                        }`}
                      >
                        {selectedProducts.includes(product.id) && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>

                      {/* Product Image */}
                      <div className="w-12 h-12 bg-gray-100 rounded-none overflow-hidden flex-shrink-0">
                        {product.images?.[0] ? (
                          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <Package className="h-5 w-5" />
                          </div>
                        )}
                      </div>

                      {/* Product Name & SKU */}
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <h3 className="font-medium text-sm text-[#5D4E3C] truncate">{product.name}</h3>
                        <p className="text-xs text-gray-400">{product.sku || '-'}</p>
                      </div>

                      {/* Price */}
                      <div className="text-right flex-shrink-0 ml-1">
                        <span className="font-bold text-[#5D4E3C]">
                          {formatEUR(product.price)}
                        </span>
                        {product.price_type === 'per_quantity' && product.price_quantity > 1 && (
                          <span className="text-[10px] text-gray-500 block">/{product.price_quantity} St.</span>
                        )}
                      </div>
                    </div>

                    {/* Bottom Row: Info Tags & Actions */}
                    <div className="flex items-center justify-between mt-2 pl-7">
                      {/* Info Tags */}
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] px-1.5 py-0.5 bg-[#F5EDE4] text-[#8B7355] rounded-none">
                          {product.stock}
                        </span>
                        {!product.is_active && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded-none">Off</span>
                        )}
                      </div>

                      {/* Actions - 4 buttons mit Tooltip-Title */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          onClick={() => handleEdit(product)} 
                          size="sm" 
                          title="Bearbeiten"
                          aria-label={`„${product.name}" bearbeiten`}
                          className="h-7 px-2 rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0 text-xs"
                          data-testid={`product-edit-${product.id}`}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          onClick={() => handleToggleActive(product)}
                          size="sm"
                          title={product.is_active ? 'Deaktivieren (ausblenden)' : 'Aktivieren (sichtbar machen)'}
                          aria-label={product.is_active ? `„${product.name}" deaktivieren` : `„${product.name}" aktivieren`}
                          className={`h-7 px-2 rounded-none border-0 text-xs ${product.is_active 
                            ? 'bg-[#FFF3E0] text-[#FF9800] hover:bg-[#FFE0B2]' 
                            : 'bg-[#E3F2FD] text-[#2196F3] hover:bg-[#BBDEFB]'}`}
                          data-testid={`product-toggle-${product.id}`}
                        >
                          {product.is_active ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </Button>
                        <Button
                          onClick={() => handleDuplicate(product.id)}
                          size="sm"
                          title="Duplizieren (Kopie erstellen)"
                          aria-label={`„${product.name}" duplizieren`}
                          className="h-7 px-2 rounded-none bg-[#F3E5F5] text-[#9C27B0] hover:bg-[#E1BEE7] border-0 text-xs"
                          data-testid={`product-duplicate-${product.id}`}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          onClick={() => { setBulkImageProduct(product); setShowBulkImageUpload(true); }}
                          size="sm"
                          title="Mehrere Bilder auf einmal hinzufügen"
                          aria-label={`Bilder zu „${product.name}" hinzufügen`}
                          className="h-7 px-2 rounded-none bg-[#E0F7FA] text-[#00838F] hover:bg-[#B2EBF2] border-0 text-xs"
                          data-testid={`product-add-images-${product.id}`}
                        >
                          <ImageIcon className="h-3 w-3" />
                        </Button>
                        <Button
                          onClick={() => handleLabelSingle(product)}
                          size="sm"
                          title="Etikett-PDF (40×30 mm) öffnen"
                          aria-label={`Etikett für „${product.name}" drucken`}
                          className="h-7 px-2 rounded-none bg-[#FFF8E6] text-[#A07F2E] hover:bg-[#FFEFCC] border-0 text-xs"
                          data-testid={`product-label-${product.id}`}
                        >
                          <Tag className="h-3 w-3" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteSingle(product)}
                          size="sm"
                          title="Löschen (unwiderruflich)"
                          aria-label={`„${product.name}" löschen`}
                          className="h-7 px-2 rounded-none bg-[#FFEBEE] text-[#E53935] hover:bg-[#FFCDD2] border-0 text-xs"
                          data-testid={`product-delete-${product.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between mt-4 px-2 gap-2">
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  Seite {currentPage}/{totalPages} • {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} von {filteredProducts.length}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="h-8 w-8 p-0 rounded-none border-[#D4C4B0]"
                  >
                    <ChevronFirst className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="h-8 w-8 p-0 rounded-none border-[#D4C4B0]"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={`h-8 w-8 p-0 rounded-none ${
                          currentPage === pageNum 
                            ? 'bg-[#C4A084] hover:bg-[#B09070] border-[#C4A084]' 
                            : 'border-[#D4C4B0]'
                        }`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 p-0 rounded-none border-[#D4C4B0]"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 p-0 rounded-none border-[#D4C4B0]"
                  >
                    <ChevronLast className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Product Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#F5EDE4' }}>
            <DialogHeader className="pb-2">
              <div className="p-2 px-3 flex items-center gap-2 rounded-none -mx-6 -mt-2" style={{ backgroundColor: '#C4A084' }}>
                <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFFEF5]">
                  <Package className="h-4 w-4 text-[#D4B030]" />
                </div>
                <DialogTitle className="text-white">{editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt'}</DialogTitle>
              </div>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              
              {/* Grundinformationen Section */}
              <div className="p-2 px-3 flex items-center gap-2 rounded-none" style={{ backgroundColor: '#C4A084' }}>
                <div className="w-5 h-5 rounded flex items-center justify-center bg-[#E8F5E9]">
                  <Edit2 className="h-3 w-3 text-[#4CAF50]" />
                </div>
                <span className="text-sm font-semibold text-white">Grundinformationen</span>
              </div>
              
              <div className="p-3 border border-[#D4C4B0] rounded-none bg-white">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="col-span-full">
                    <Label className="text-[#5D4E3C] text-sm block">Produktname&nbsp;*</Label>
                    <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="mt-1 w-full rounded-none border-[#D4C4B0] focus:ring-[#C4A084]" />
                  </div>
                  <div className="col-span-full">
                    <Label className="text-[#5D4E3C] text-sm block">Beschreibung&nbsp;*</Label>
                    <Textarea required value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} className="mt-1 w-full rounded-none border-[#D4C4B0] focus:ring-[#C4A084]" />
                  </div>

                  {/* SEO Fields */}
                  <div className="col-span-full border border-[#E8DCC8] bg-[#FAF5EE] p-3 space-y-3" data-testid="seo-fields">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs text-[#6D5A47] flex items-center gap-1.5">
                        <span>🔍</span><strong>Google-Suchmaschinen-Optimierung (SEO)</strong>
                        <span className="text-[#9C8C7A]">— optional, fällt sonst auf Name &amp; Beschreibung zurück</span>
                      </p>
                      <Button
                        type="button"
                        onClick={handleGenerateSeo}
                        disabled={seoGenerating}
                        size="sm"
                        variant="outline"
                        className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F0E6D6] text-xs h-7 gap-1"
                        data-testid="seo-generate-ai-btn"
                      >
                        {seoGenerating ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Generiere…
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-3 w-3" />
                            KI-Vorschlag
                          </>
                        )}
                      </Button>
                    </div>
                    <div>
                      <Label className="text-[#5D4E3C] text-xs flex items-center justify-between">
                        <span>Google-Titel</span>
                        <span className={`text-[10px] ${(formData.meta_title?.length || 0) > 60 ? 'text-red-600' : 'text-[#9C8C7A]'}`}>
                          {formData.meta_title?.length || 0} / 60 Zeichen
                        </span>
                      </Label>
                      <Input
                        value={formData.meta_title}
                        onChange={(e) => setFormData({...formData, meta_title: e.target.value})}
                        maxLength={70}
                        placeholder={`z.B. ${formData.name || 'Rosa Haargummi mit Schleife'} · Handmade · Homeyoursoul`}
                        className="mt-1 rounded-none border-[#D4C4B0] text-sm"
                        data-testid="meta-title-input"
                      />
                      <p className="text-[10px] text-[#9C8C7A] mt-0.5">Erscheint als blauer Link in den Google-Suchergebnissen.</p>
                    </div>
                    <div>
                      <Label className="text-[#5D4E3C] text-xs flex items-center justify-between">
                        <span>Google-Beschreibung</span>
                        <span className={`text-[10px] ${(formData.meta_description?.length || 0) > 160 ? 'text-red-600' : 'text-[#9C8C7A]'}`}>
                          {formData.meta_description?.length || 0} / 160 Zeichen
                        </span>
                      </Label>
                      <Textarea
                        value={formData.meta_description}
                        onChange={(e) => setFormData({...formData, meta_description: e.target.value})}
                        maxLength={180}
                        rows={2}
                        placeholder="Locke zum Klicken: Handgemacht, hochwertige Materialien, schneller Versand, 14 Tage Rückgabe…"
                        className="mt-1 rounded-none border-[#D4C4B0] text-sm"
                        data-testid="meta-description-input"
                      />
                      <p className="text-[10px] text-[#9C8C7A] mt-0.5">Erscheint als grauer Text unter dem Titel in der Google-Suche.</p>
                    </div>
                  </div>
                  <div className="col-span-full">
                    <Label className="text-[#5D4E3C] text-sm block">Preis&nbsp;(€)&nbsp;*</Label>
                    <div className="mt-1 flex flex-col gap-2">
                      <Input type="number" step="0.01" required value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="rounded-none border-[#D4C4B0]" />
                      
                      {/* Price Type Selection */}
                      <div className="flex gap-2 items-center">
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, price_type: 'per_piece', price_quantity: 1})}
                          className={`px-3 py-1.5 text-xs rounded-none border transition-all ${
                            formData.price_type === 'per_piece'
                              ? 'bg-[#C4A084] text-white border-[#C4A084]'
                              : 'bg-white text-[#5D4E3C] border-[#D4C4B0] hover:border-[#C4A084]'
                          }`}
                        >
                          Pro Stück
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, price_type: 'per_quantity', price_quantity: formData.price_quantity || 2})}
                          className={`px-3 py-1.5 text-xs rounded-none border transition-all ${
                            formData.price_type === 'per_quantity'
                              ? 'bg-[#C4A084] text-white border-[#C4A084]'
                              : 'bg-white text-[#5D4E3C] border-[#D4C4B0] hover:border-[#C4A084]'
                          }`}
                        >
                          Pro X Stück
                        </button>
                        
                        {formData.price_type === 'per_quantity' && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-[#5D4E3C]">für</span>
                            <Input
                              type="number"
                              min="2"
                              value={formData.price_quantity}
                              onChange={(e) => setFormData({...formData, price_quantity: parseInt(e.target.value) || 2})}
                              className="w-16 h-8 text-center rounded-none border-[#D4C4B0] text-sm"
                            />
                            <span className="text-xs text-[#5D4E3C]">Stück</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Price Preview */}
                      <p className="text-xs text-[#8B7355]">
                        {formData.price_type === 'per_quantity' && formData.price_quantity > 1
                          ? `Anzeige: ${formatEUR(parseFloat(formData.price || 0))} für ${formData.price_quantity} Stück`
                          : `Anzeige: ${formatEUR(parseFloat(formData.price || 0))} pro Stück`
                        }
                      </p>

                      {/* SALE-Preis (optional) */}
                      <div className="mt-2 pt-2 border-t border-dashed border-[#D4C4B0]">
                        <Label className="text-[#5D4E3C] text-sm flex items-center gap-1.5">
                          <span className="text-[10px] px-1.5 py-0.5 bg-[#B0512E] text-white tracking-wider">SALE</span>
                          SALE-Preis (optional)
                        </Label>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="z. B. 1.99"
                            value={formData.sale_price ?? ''}
                            onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                            className="flex-1 rounded-none border-[#D4C4B0]"
                            data-testid="sale-price-input"
                          />
                          {formData.sale_price && parseFloat(formData.sale_price) > 0 && parseFloat(formData.price) > 0 && parseFloat(formData.sale_price) < parseFloat(formData.price) && (
                            <span className="text-xs font-bold text-[#B0512E] whitespace-nowrap">
                              −{Math.round((1 - parseFloat(formData.sale_price) / parseFloat(formData.price)) * 100)}%
                            </span>
                          )}
                          {formData.sale_price && (
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, sale_price: '', sale_starts_at: '', sale_ends_at: '' })}
                              className="text-xs text-[#8B7355] underline hover:text-[#5D4E3C]"
                              data-testid="sale-price-clear"
                            >
                              entfernen
                            </button>
                          )}
                        </div>
                        {/* Optional: zeitlich befristete Aktion */}
                        {formData.sale_price && (
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px] text-[#8B7355] uppercase tracking-wider">
                                Start (optional)
                              </Label>
                              <Input
                                type="datetime-local"
                                value={formData.sale_starts_at ?? ''}
                                onChange={(e) => setFormData({ ...formData, sale_starts_at: e.target.value })}
                                className="rounded-none border-[#D4C4B0] text-sm h-9"
                                data-testid="sale-starts-at-input"
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] text-[#8B7355] uppercase tracking-wider">
                                Ende (optional)
                              </Label>
                              <Input
                                type="datetime-local"
                                value={formData.sale_ends_at ?? ''}
                                onChange={(e) => setFormData({ ...formData, sale_ends_at: e.target.value })}
                                className="rounded-none border-[#D4C4B0] text-sm h-9"
                                data-testid="sale-ends-at-input"
                              />
                            </div>
                          </div>
                        )}
                        <p className="text-[10px] text-[#9C8C7A] mt-1">
                          Wenn gesetzt, erscheint der Artikel automatisch im SALE-Bereich (/sale).
                          Start/Ende sind optional — leer = sofort aktiv, kein Ablauf.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#5D4E3C] text-sm block">Lagerbestand</Label>
                    <Input type="number" value={formData.stock} onChange={(e) => setFormData({...formData, stock: e.target.value})} className="mt-1 rounded-none border-[#D4C4B0]" />
                  </div>
                  <div>
                    <Label className="text-[#5D4E3C] text-sm block">Kategorie&nbsp;*</Label>
                    {!showNewCategoryInput ? (
                      <div className="space-y-2 mt-1">
                        <select
                          required
                          value={formData.category}
                          onChange={(e) => {
                            if (e.target.value === '__NEW__') {
                              setShowNewCategoryInput(true);
                              setFormData({...formData, category: ''});
                            } else {
                              setFormData({...formData, category: e.target.value});
                            }
                          }}
                          className="w-full px-3 py-2 border border-[#D4C4B0] rounded-none focus:outline-none focus:ring-2 focus:ring-[#C4A084] bg-white text-[#5D4E3C]"
                        >
                          <option value="">-- Kategorie wählen --</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.name}>
                              {cat.name}
                            </option>
                          ))}
                          <option value="__NEW__" className="font-semibold text-[#C4A084]">
                            ➕ Neue Kategorie erstellen
                          </option>
                        </select>
                      </div>
                    ) : (
                      <div className="space-y-2 mt-1">
                        <div className="flex gap-2">
                          <Input
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="Neue Kategorie"
                            className="rounded-none border-[#D4C4B0]"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleCreateNewCategory();
                              }
                            }}
                          />
                          <Button
                            type="button"
                            onClick={handleCreateNewCategory}
                            size="sm"
                            className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                          >
                            ✓
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                              setShowNewCategoryInput(false);
                              setNewCategoryName('');
                            }}
                            className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0"
                          >
                            ✗
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-[#5D4E3C] text-sm">Unterkategorie</Label>
                    {subcategoryConfig[formData.category] && subcategoryConfig[formData.category].length > 0 ? (
                      <select
                        value={formData.subcategory}
                        onChange={(e) => setFormData({...formData, subcategory: e.target.value})}
                        className="mt-1 rounded-none border border-[#D4C4B0] bg-white h-10 w-full px-3 focus:outline-none focus:border-[#C4A084]"
                      >
                        <option value="">— wählen —</option>
                        {subcategoryConfig[formData.category].map((sub) => (
                          <option key={sub} value={sub}>{sub}</option>
                        ))}
                      </select>
                    ) : (
                      <Input value={formData.subcategory} onChange={(e) => setFormData({...formData, subcategory: e.target.value})} className="mt-1 rounded-none border-[#D4C4B0]" />
                    )}
                  </div>
                  <div>
                    <Label className="text-[#5D4E3C] text-sm">SKU / Artikelnummer</Label>
                    <Input value={formData.sku} onChange={(e) => setFormData({...formData, sku: e.target.value})} className="mt-1 rounded-none border-[#D4C4B0]" />
                  </div>
                  <div>
                    <Label className="text-[#5D4E3C] text-sm">Einheit</Label>
                    <select
                      value={formData.unit || 'Stück'}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className="mt-1 w-full border border-[#D4C4B0] bg-white px-3 py-2 text-sm rounded-none"
                      data-testid="product-unit-select"
                    >
                      <option value="Stück">Stück</option>
                      <option value="Paar">Paar</option>
                      <option value="Set">Set</option>
                      <option value="Packung">Packung</option>
                      <option value="kg">kg</option>
                      <option value="g">g</option>
                      <option value="l">l</option>
                      <option value="ml">ml</option>
                      <option value="m">m</option>
                      <option value="cm">cm</option>
                      <option value="m²">m²</option>
                    </select>
                    <p className="text-[10px] text-[#8B7355] mt-1">Erscheint auf der Rechnung als Maßeinheit.</p>
                  </div>
                  <div className="flex items-center space-x-2 pt-6">
                    <Switch checked={formData.is_handmade} onCheckedChange={(checked) => setFormData({...formData, is_handmade: checked})} />
                    <Label className="text-[#5D4E3C] text-sm">Handgemacht</Label>
                  </div>
                </div>

                {/* Lieferzeit */}
                <div className="mt-3 p-3 border border-[#E8DCC8] bg-[#FAF5EE]" data-testid="delivery-time-field">
                  <Label className="text-[#5D4E3C] text-sm font-semibold mb-2 block">🚚 Lieferzeit</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <label
                      className={`flex-1 cursor-pointer p-2.5 border text-sm transition-colors ${
                        formData.delivery_time === '1-3'
                          ? 'border-[#5D4E3C] bg-white text-[#5D4E3C] font-semibold'
                          : 'border-[#D4C4B0] bg-white/50 text-[#8B7355]'
                      }`}
                      data-testid="delivery-time-1-3"
                    >
                      <input
                        type="radio"
                        name="delivery_time"
                        value="1-3"
                        checked={formData.delivery_time === '1-3'}
                        onChange={(e) => setFormData({...formData, delivery_time: e.target.value})}
                        className="mr-2"
                      />
                      <strong>1–3 Werktage</strong> · auf Lager / Standard
                    </label>
                    <label
                      className={`flex-1 cursor-pointer p-2.5 border text-sm transition-colors ${
                        formData.delivery_time === '3-7'
                          ? 'border-[#5D4E3C] bg-white text-[#5D4E3C] font-semibold'
                          : 'border-[#D4C4B0] bg-white/50 text-[#8B7355]'
                      }`}
                      data-testid="delivery-time-3-7"
                    >
                      <input
                        type="radio"
                        name="delivery_time"
                        value="3-7"
                        checked={formData.delivery_time === '3-7'}
                        onChange={(e) => setFormData({...formData, delivery_time: e.target.value})}
                        className="mr-2"
                      />
                      <strong>3–7 Werktage</strong> · handgemacht / auf Bestellung
                    </label>
                  </div>
                  <p className="text-[10px] text-[#8B7355] mt-1.5">
                    Für handgemachte Artikel, die nicht auf Vorrat sind und individuell neu gefertigt werden, wähle 3–7 Werktage.
                  </p>
                </div>
              </div>

              {/* Farbvarianten Section */}
              <div className="p-2 px-3 flex items-center gap-2 rounded-none" style={{ backgroundColor: '#C4A084' }}>
                <div className="w-5 h-5 rounded flex items-center justify-center bg-[#F3E5F5]">
                  <Palette className="h-3 w-3 text-[#9C27B0]" />
                </div>
                <span className="text-sm font-semibold text-white">Farbvarianten (optional)</span>
              </div>
              
              <div className="p-3 border border-[#D4C4B0] rounded-none bg-white">
                <div className="flex justify-end mb-3">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      setColorVariants([...colorVariants, {
                        color_name: '',
                        color_code: '#000000',
                        price_adjustment: 0,
                        stock: 0,
                        image: ''
                      }]);
                    }}
                    className="rounded-none bg-[#FCE4EC] text-[#E91E63] hover:bg-[#F8BBD9] border-0"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Hinzufügen
                  </Button>
                </div>
                
                {colorVariants.length > 0 ? (
                  <div className="space-y-3">
                    {colorVariants.map((variant, index) => (
                      <div key={index} className="border border-[#D4C4B0] rounded-none p-3 space-y-2" style={{ backgroundColor: '#FAF8F5' }}>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-5 h-5 rounded-full border border-[#D4C4B0]"
                              style={{ backgroundColor: variant.color_code }}
                            />
                            <span className="font-medium text-[#5D4E3C] text-sm">Farbe {index + 1}: {variant.color_name || '(Unbenannt)'}</span>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                              const newVariants = colorVariants.filter((_, i) => i !== index);
                              setColorVariants(newVariants);
                            }}
                            className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0 h-7 px-2"
                          >
                            Entfernen
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[#8B7355] text-xs">Farbname *</Label>
                            <Input
                              value={variant.color_name}
                              onChange={(e) => {
                                const newVariants = [...colorVariants];
                                newVariants[index].color_name = e.target.value;
                                setColorVariants(newVariants);
                              }}
                              placeholder="z.B. Schwarz"
                              className="mt-1 rounded-none border-[#D4C4B0] h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-[#8B7355] text-xs">Farbcode</Label>
                            <div className="flex gap-1 mt-1">
                              <Input
                                type="color"
                                value={variant.color_code}
                                onChange={(e) => {
                                  const newVariants = [...colorVariants];
                                  newVariants[index].color_code = e.target.value;
                                  setColorVariants(newVariants);
                                }}
                                className="w-10 h-8 p-0.5 rounded-none border-[#D4C4B0]"
                              />
                              <Input
                                value={variant.color_code}
                                onChange={(e) => {
                                  const newVariants = [...colorVariants];
                                  newVariants[index].color_code = e.target.value;
                                  setColorVariants(newVariants);
                                }}
                                className="flex-1 rounded-none border-[#D4C4B0] h-8 text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-[#8B7355] text-xs">Preisanpassung (€)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={variant.price_adjustment}
                              onChange={(e) => {
                                const newVariants = [...colorVariants];
                                newVariants[index].price_adjustment = parseFloat(e.target.value) || 0;
                                setColorVariants(newVariants);
                              }}
                              className="mt-1 rounded-none border-[#D4C4B0] h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-[#8B7355] text-xs">Lagerbestand</Label>
                            <Input
                              type="number"
                              value={variant.stock}
                              onChange={(e) => {
                                const newVariants = [...colorVariants];
                                newVariants[index].stock = parseInt(e.target.value) || 0;
                                setColorVariants(newVariants);
                              }}
                              className="mt-1 rounded-none border-[#D4C4B0] h-8 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#8B7355] text-center py-3">Keine Farbvarianten hinzugefügt</p>
                )}
              </div>

              {/* Produktbilder Section */}
              <div className="p-2 px-3 flex items-center gap-2 rounded-none" style={{ backgroundColor: '#C4A084' }}>
                <div className="w-5 h-5 rounded flex items-center justify-center bg-[#FFF3E0]">
                  <ImageIcon className="h-3 w-3 text-[#FF9800]" />
                </div>
                <span className="text-sm font-semibold text-white">Produktbilder</span>
              </div>
              
              <div className="p-3 border border-[#D4C4B0] rounded-none bg-white">
                {/* Upload Options - Grid Layout */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {/* File Upload */}
                  <div className="border-2 border-dashed border-[#D4C4B0] rounded-none p-4 text-center hover:border-[#C4A084] transition-colors" style={{ backgroundColor: '#FAF8F5' }}>
                    <input
                      type="file"
                      id="image-upload"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                    <label
                      htmlFor="image-upload"
                      className={`cursor-pointer flex flex-col items-center ${uploadingImage ? 'opacity-50' : ''}`}
                    >
                      <Upload className="w-8 h-8 text-[#C4A084] mb-2" />
                      <span className="text-sm text-[#8B7355]">
                        {uploadingImage ? 'Lädt...' : 'Hochladen'}
                      </span>
                      <span className="text-xs text-[#8B7355] mt-1">
                        Galerie
                      </span>
                    </label>
                  </div>

                  {/* Camera Capture - Native Input for iOS/Android */}
                  <div className="border-2 border-dashed border-[#D4C4B0] rounded-none p-4 text-center hover:border-[#C4A084] transition-colors" style={{ backgroundColor: '#FAF8F5' }}>
                    <input
                      type="file"
                      id="camera-capture"
                      accept="image/*"
                      capture="environment"
                      onChange={handleCameraCapture}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                    <label
                      htmlFor="camera-capture"
                      className={`cursor-pointer flex flex-col items-center ${uploadingImage ? 'opacity-50' : ''}`}
                    >
                      <Camera className="w-8 h-8 text-[#C4A084] mb-2" />
                      <span className="text-sm text-[#8B7355]">
                        {uploadingImage ? 'Lädt...' : 'Kamera'}
                      </span>
                      <span className="text-xs text-[#8B7355] mt-1">
                        Foto aufnehmen
                      </span>
                    </label>
                  </div>
                </div>

                {/* Current Images Preview */}
                {formData.images && (
                  <>
                    <div className="mt-3 flex items-center justify-between">
                      <p className="text-xs text-[#8B7355]">
                        💡 Ziehen zum Umsortieren · Position 1 = Titelbild
                      </p>
                      <p className="text-xs text-[#5D4E3C] font-medium">
                        {formData.images.split('\n').filter(i => i.trim()).length} Bild(er)
                      </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                    {formData.images.split('\n').filter(i => i.trim()).map((img, idx) => (
                      <div
                        key={`${img}-${idx}`}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', String(idx));
                          setDraggedIdx(idx);
                        }}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                        onDragEnter={() => setDragOverIdx(idx)}
                        onDragLeave={() => setDragOverIdx(prev => prev === idx ? null : prev)}
                        onDragEnd={() => { setDraggedIdx(null); setDragOverIdx(null); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const from = Number(e.dataTransfer.getData('text/plain'));
                          const to = idx;
                          setDraggedIdx(null);
                          setDragOverIdx(null);
                          if (from === to || Number.isNaN(from)) return;
                          const arr = formData.images.split('\n').filter(i => i.trim());
                          const [moved] = arr.splice(from, 1);
                          arr.splice(to, 0, moved);
                          setFormData({...formData, images: arr.join('\n')});
                        }}
                        className={`relative group rounded border overflow-hidden cursor-move transition-all ${
                          draggedIdx === idx ? 'opacity-40 scale-95' : 'opacity-100'
                        } ${
                          dragOverIdx === idx && draggedIdx !== idx
                            ? 'border-[#8B6F4D] border-2 shadow-md'
                            : 'border-[#D4C4B0]'
                        }`}
                        data-testid={`image-preview-${idx}`}
                      >
                        {idx === 0 && (
                          <div className="absolute top-1 left-1 z-10 bg-[#8B6F4D] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-none">
                            ★ TITELBILD
                          </div>
                        )}
                        <div className="absolute top-1 right-1 z-10 bg-white/80 text-[#5D4E3C] text-[10px] px-1 rounded-none font-mono">
                          {idx + 1}
                        </div>
                        <img
                          src={img}
                          alt={`Produktbild ${idx + 1}`}
                          className="w-full h-24 object-cover pointer-events-none"
                        />
                        {/* Always visible buttons at bottom */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 flex items-center justify-center gap-1 py-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              if (idx === 0) return;
                              const arr = formData.images.split('\n').filter(i => i.trim());
                              [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                              setFormData({...formData, images: arr.join('\n')});
                            }}
                            disabled={idx === 0}
                            className="bg-white/90 hover:bg-white text-[#5D4E3C] rounded px-1.5 py-1 text-xs disabled:opacity-30"
                            title="Nach vorne"
                            data-testid={`image-move-up-${idx}`}
                          >
                            ‹
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const arr = formData.images.split('\n').filter(i => i.trim());
                              if (idx === arr.length - 1) return;
                              [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                              setFormData({...formData, images: arr.join('\n')});
                            }}
                            disabled={idx === formData.images.split('\n').filter(i => i.trim()).length - 1}
                            className="bg-white/90 hover:bg-white text-[#5D4E3C] rounded px-1.5 py-1 text-xs disabled:opacity-30"
                            title="Nach hinten"
                            data-testid={`image-move-down-${idx}`}
                          >
                            ›
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEditExistingImage(img, idx)}
                            className="bg-[#AF9774] hover:bg-[#C4A084] text-white rounded px-2 py-1 flex items-center gap-1 text-xs"
                            title="Bild bearbeiten"
                          >
                            <Wand2 className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const images = formData.images.split('\n').filter(i => i.trim());
                              images.splice(idx, 1);
                              setFormData({...formData, images: images.join('\n')});
                            }}
                            className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                            title="Bild entfernen"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                    </div>
                  </>
                )}

                {/* Manual URL Input (advanced option) */}
                <details className="mt-3">
                  <summary className="text-sm text-[#8B7355] cursor-pointer hover:text-[#5D4E3C]">
                    Erweitert: Bild-URLs manuell eingeben
                  </summary>
                  <Textarea
                    value={formData.images}
                    onChange={(e) => setFormData({...formData, images: e.target.value})}
                    rows={3}
                    placeholder="https://..."
                    className="mt-2 rounded-none border-[#D4C4B0]"
                  />
                  <p className="text-xs text-[#8B7355] mt-1">Eine URL pro Zeile</p>
                </details>
              </div>

              {/* Produktmaße Section */}
              <div className="p-2 px-3 flex items-center gap-2 rounded-none" style={{ backgroundColor: '#C4A084' }}>
                <div className="w-5 h-5 rounded flex items-center justify-center bg-[#E3F2FD]">
                  <Ruler className="h-3 w-3 text-[#2196F3]" />
                </div>
                <span className="text-sm font-semibold text-white">Produktmaße (für Versand)</span>
              </div>
              
              <div className="p-3 border border-[#D4C4B0] rounded-none bg-white">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-[#8B7355] text-xs">Gewicht (g)</Label>
                    <Input type="number" value={formData.weight} onChange={(e) => setFormData({...formData, weight: e.target.value})} className="mt-1 rounded-none border-[#D4C4B0] h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-[#8B7355] text-xs">Länge (cm)</Label>
                    <Input type="number" step="0.1" value={formData.length} onChange={(e) => setFormData({...formData, length: e.target.value})} className="mt-1 rounded-none border-[#D4C4B0] h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-[#8B7355] text-xs">Breite (cm)</Label>
                    <Input type="number" step="0.1" value={formData.width} onChange={(e) => setFormData({...formData, width: e.target.value})} className="mt-1 rounded-none border-[#D4C4B0] h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-[#8B7355] text-xs">Höhe (cm)</Label>
                    <Input type="number" step="0.1" value={formData.height} onChange={(e) => setFormData({...formData, height: e.target.value})} className="mt-1 rounded-none border-[#D4C4B0] h-8 text-sm" />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button 
                  type="submit" 
                  className="flex-1 rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0 font-medium"
                >
                  Speichern
                </Button>
                {editingProduct && (
                  <Button
                    type="button"
                    onClick={() => handleLabelSingle(editingProduct)}
                    title="Etikett-PDF (40×30 mm) jetzt öffnen"
                    className="flex-1 sm:flex-initial rounded-none bg-[#FFF8E6] text-[#A07F2E] hover:bg-[#FFEFCC] border-0 font-medium flex items-center justify-center gap-1.5"
                    data-testid="editor-print-label"
                  >
                    <Tag className="h-4 w-4" /> Etikett drucken
                  </Button>
                )}
                <Button 
                  type="button" 
                  onClick={() => setShowDialog(false)} 
                  className="flex-1 sm:flex-initial rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0"
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Duplicate Warning Dialog */}
      {showDuplicateWarning && (
        <Dialog open={showDuplicateWarning} onOpenChange={setShowDuplicateWarning}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-red-600">⚠️ Mögliches Duplikat erkannt</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700">
                Es wurden bereits ähnliche Produkte gefunden. Möchten Sie diesen Artikel dennoch hinzufügen?
              </p>
              
              {duplicateProducts.map((product, index) => (
                <div key={product.id} className="border rounded-lg p-4 bg-yellow-50">
                  <div className="flex gap-4">
                    {product.images && product.images[0] && (
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-20 h-20 object-cover rounded"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{product.name}</h4>
                      <p className="text-sm text-gray-600">{product.category}</p>
                      <p className="text-sm font-bold text-gray-900 mt-1">{formatEUR(product.price)}</p>
                      {product.color_variants && product.color_variants.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Farben: {product.color_variants.map(v => v.color_name).join(', ')}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {product.match_reasons.map((reason, i) => (
                          <span key={i} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                            {reason}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button
                  onClick={() => {
                    setShowDuplicateWarning(false);
                    setPendingSubmit(null);
                  }}
                  className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleConfirmDuplicate}
                  className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                >
                  Ja, dennoch hinzufügen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Category Change Dialog */}
      <Dialog open={showBulkActions} onOpenChange={setShowBulkActions}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#F5EDE4' }}>
          <DialogHeader>
            <DialogTitle className="text-[#5D4E3C]">Kategorie ändern für {selectedProducts.length} Produkt(e)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#5D4E3C]">Neue Kategorie wählen</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    onClick={() => handleBulkMoveToCategory(category.name)}
                    className="justify-start rounded-none bg-white text-[#5D4E3C] hover:bg-[#F5EDE4] border border-[#D4C4B0]"
                  >
                    <FolderOpen className="h-4 w-4 mr-2" />
                    {category.name}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => setShowBulkActions(false)}
                className="flex-1 rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0"
              >
                Abbrechen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Actions Toolbar */}
      <BulkActionsToolbar
        selectedCount={selectedProducts.length}
        selectedIds={selectedProducts}
        categories={categories}
        onClearSelection={() => setSelectedProducts([])}
        onSelectAll={handleSelectAll}
        totalCount={filteredProducts.length}
        allSelected={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
        onRefresh={fetchProducts}
      />
      
      {/* Merge Products Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" style={{ backgroundColor: '#F5EDE4' }}>
          <DialogHeader>
            <DialogTitle className="text-[#5D4E3C]">
              Artikel als Farbvarianten zusammenführen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-[#5D4E3C]">
              Wähle den <strong>Haupt-Artikel</strong>. Die anderen werden als <strong>Farbvarianten</strong> in diesen integriert und danach gelöscht. Ihre Bilder und Bestände bleiben erhalten.
            </p>

            {selectedProducts.length >= 2 && (
              <div className="space-y-3">
                {selectedProducts.map(id => {
                  const p = products.find(pp => pp.id === id);
                  if (!p) return null;
                  const isMaster = mergeKeepId === id;
                  return (
                    <div
                      key={id}
                      className="flex items-start gap-3 bg-white border-2 p-3"
                      style={{ borderColor: isMaster ? '#5D4E3C' : '#E8DCC8' }}
                      data-testid={`merge-row-${id}`}
                    >
                      <input
                        type="radio"
                        name="merge-master"
                        checked={isMaster}
                        onChange={() => setMergeKeepId(id)}
                        className="mt-1.5"
                        data-testid={`merge-master-radio-${id}`}
                      />
                      {p.images?.[0] && (
                        <img src={p.images[0]} alt={p.name} className="w-14 h-14 object-cover" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{p.name}</span>
                          {isMaster && (
                            <span className="text-[10px] bg-[#5D4E3C] text-white px-2 py-0.5 rounded">
                              HAUPT-ARTIKEL
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#8B7355]">
                          {p.price?.toFixed?.(2)}€  ·  SKU {p.sku}  ·  {p.stock ?? 0} Stück
                        </div>
                        {!isMaster && (
                          <div className="mt-2">
                            <Label className="text-xs text-[#5D4E3C]">Farb-/Variantenname:</Label>
                            <Input
                              value={mergeColorOverrides[id] || ''}
                              onChange={(e) => setMergeColorOverrides({
                                ...mergeColorOverrides,
                                [id]: e.target.value,
                              })}
                              placeholder="z.B. rot, schwarz, gross"
                              className="h-8 text-sm mt-1"
                              data-testid={`merge-color-input-${id}`}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <strong>Hinweis:</strong> Diese Aktion ist nicht umkehrbar. Die nicht-gewählten Artikel werden gelöscht. Ihre Bilder werden in den Haupt-Artikel übernommen (max. 12).
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowMergeDialog(false)}
              data-testid="merge-cancel-btn"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleConfirmMerge}
              className="bg-[#5D4E3C] hover:bg-[#3E2723] text-white"
              data-testid="merge-confirm-btn"
            >
              Zusammenführen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Image Editor */}
      <ProductImageEditor
        isOpen={showImageEditor}
        onClose={() => {
          setShowImageEditor(false);
          setEditingImageFile(null);
          setEditingImageUrl('');
          setEditingImageIndex(null);
        }}
        imageFile={editingImageFile}
        imageUrl={editingImageUrl}
        onSave={handleSaveEditedImage}
      />
      
      {/* Batch Image Processor */}
      <BatchImageProcessor
        isOpen={showBatchProcessor}
        onClose={() => setShowBatchProcessor(false)}
        products={products}
        onSaveResults={async (results) => {
          // Save processed images back to products
          try {
            for (const result of results) {
              if (result.success && result.processed_image && result.product_id) {
                // Convert base64 to blob and upload
                const response = await fetch(result.processed_image);
                const blob = await response.blob();
                
                const formDataUpload = new FormData();
                formDataUpload.append('file', blob, 'processed-image.png');
                
                const uploadResponse = await axiosInstance.post('/uploads/upload', formDataUpload, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                });
                
                if (uploadResponse.data.url) {
                  let imageUrl = uploadResponse.data.url;
                  if (imageUrl.startsWith('/api')) {
                    imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
                  }
                  
                  // Update product with new image
                  const product = products.find(p => p.id === result.product_id);
                  if (product) {
                    const newImages = [...(product.images || [])];
                    if (result.image_index !== undefined && result.image_index < newImages.length) {
                      newImages[result.image_index] = imageUrl;
                    }
                    
                    await axiosInstance.put(`/products/${result.product_id}`, {
                      ...product,
                      images: newImages
                    });
                  }
                }
              }
            }
            toast.success('Bearbeitete Bilder wurden gespeichert!');
            fetchProducts();
          } catch (error) {
            console.error('Error saving batch results:', error);
            toast.error('Fehler beim Speichern der Bilder');
          }
        }}
      />
      </div>
    </div>
  );
};

export default AdminProducts;